"use client";

import React from "react";
import { MessageSquare, Phone, Send } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="programconsultationctastrip" data-nav="light" className="relative w-full bg-background pt-12 sm:pt-14 md:pt-16 pb-8 md:pb-10 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.35,
        ease: "easeOut"
      }} className="relative rounded-xl border border-border bg-card p-5 sm:p-6 lg:px-8 lg:py-6 shadow-sm text-card-foreground">
          {/* Top precision spec-rail line with accent square notch */}
          <div className="absolute top-0 inset-x-0 h-[2px] bg-secondary/30 rounded-t-xl" />
          <div className="absolute top-0 left-8 sm:left-12 size-1.5 bg-secondary -translate-y-1/2" />
          <div className="absolute top-0 right-8 sm:right-12 size-1.5 bg-accent -translate-y-1/2" />

          {/* Floating decorative mascot cheering on top of the card */}
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/cb4ea757-3edb-4585-b971-0b00ff0ff860.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/cb4ea757-3edb-4585-b971-0b00ff0ff860.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/cb4ea757-3edb-4585-b971-0b00ff0ff860.png-wvc-sizes" alt="Mascot ROBOTO giơ nắm đấm cổ vũ với khẩu hiệu Luôn nỗ lực để tiến bộ" loading="lazy" className="absolute bottom-[calc(100%-10px)] right-4 sm:right-6 lg:right-10 z-10 w-20 sm:w-24 md:w-28 lg:w-32 h-auto object-contain pointer-events-none select-none hidden sm:block" />

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Left: Heading prompt */}
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary/10 text-secondary">
                <MessageSquare className="size-5" />
              </div>
              <div>
                <h3 className="font-serif text-lg sm:text-xl lg:text-2xl font-semibold tracking-tight text-foreground leading-snug">
                  KHÔNG CHẮC CON NÊN BẮT ĐẦU TỪ CHƯƠNG TRÌNH NÀO?
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                  Đội ngũ học thuật ROBOTO sẵn sàng hỗ trợ định hướng lộ trình phù hợp theo độ tuổi và năng lực.
                </p>
              </div>
            </div>

            {/* Right: Action cluster */}
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 lg:gap-4 shrink-0">
              {/* Primary Consultation Button */}
              <Button asChild className="w-full sm:w-auto bg-accent text-accent-foreground font-semibold px-5 py-2.5 h-auto rounded-lg shadow-sm hover:bg-accent/90 transition-[background-color,box-shadow]">
                <Link to="/FreeTrialContactPage">
                  <span>ĐĂNG KÝ TƯ VẤN</span>
                </Link>
              </Button>

              <Separator orientation="vertical" className="hidden xl:block h-8 bg-border" />

              {/* Quick Contact Options */}
              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto pt-2 sm:pt-0 border-t border-border sm:border-t-0">
                {/* Zalo Disabled Pill */}
                <div className="flex items-center gap-1.5 rounded-md border border-border bg-muted/50 px-2.5 py-1.5 text-xs text-muted-foreground cursor-not-allowed opacity-80" aria-disabled="true">
                  <Send className="size-3.5" />
                  <span className="font-medium">Zalo</span>
                  <Badge variant="outline" className="border-border text-[10px] py-0 px-1 font-normal text-muted-foreground">
                    Sắp cập nhật
                  </Badge>
                </div>

                {/* Messenger Disabled Pill */}
                <div className="flex items-center gap-1.5 rounded-md border border-border bg-muted/50 px-2.5 py-1.5 text-xs text-muted-foreground cursor-not-allowed opacity-80" aria-disabled="true">
                  <MessageSquare className="size-3.5" />
                  <span className="font-medium">Messenger</span>
                  <Badge variant="outline" className="border-border text-[10px] py-0 px-1 font-normal text-muted-foreground">
                    Sắp cập nhật
                  </Badge>
                </div>

                {/* Hotline Active Link */}
                <a href="tel:0346386986" className="inline-flex items-center gap-1.5 rounded-md border border-secondary/30 bg-secondary/10 px-2.5 py-1.5 text-xs font-semibold text-secondary hover:bg-secondary/20 transition-[background-color,border-color] shrink-0">
                  <Phone className="size-3.5 text-secondary" />
                  <span>0346 386 986</span>
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}